import React, { useMemo, useState } from "react";
import { Column } from "react-table";
import styled from "styled-components";
import {
  Alert,
  Button,
  Callout,
  Card,
  Collapse,
  InputGroup,
} from "@blueprintjs/core";
import { faJira, faSlack } from "@fortawesome/free-brands-svg-icons";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mantine/core";

import { DataTable } from "@components";

import {
  IntegrationType,
  SlackAppIntegration,
  SlackAppNotificationMapping,
} from "@pages/settings/types";

const CONFIG = {
  [IntegrationType.SlackApp]: {
    name: "Slack",
    icon: faSlack,
  },
  [IntegrationType.Jira]: {
    name: "Jira",
    icon: faJira,
  },
};

const IntegrationCard = styled(Card)`
  padding: 1rem;
  &:not(:first-child) {
    margin-top: 1.2rem;
  }
`;

const Label = styled.span`
  min-width: 6em;
`;

const Info = styled(Callout)`
  padding: 0.3em;
  margin-top: 0.7em;
`;

const InputLabel = styled.span`
  color: #777;
  min-width: 6em;
`;

const WideInput = styled(InputGroup)`
  width: 100%;
  flex-grow: 1;
  display: flex;
  & > .bp3-input {
    color: gray;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: none;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7em;
  & > :not(:first-child) {
    margin-left: 1em;
  }
  & {
    white-space: nowrap;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: none;
  flex-grow: 1;
  align-items: center;
  & > :not(:first-child) {
    margin-left: 1em;
  }
`;

type Props = {
  integration: SlackAppIntegration;
  mappings: SlackAppNotificationMapping[];
  onDelete: () => void;
  onUpdate: () => void;
  testMapping: (channel_id: string) => void;
  removeMapping: (channel_id: string) => void;
};

export const IntegrationSlackAppCard: React.FC<Props> = ({
  integration,
  mappings,
  onDelete,
  onUpdate,
  testMapping,
  removeMapping,
}) => {
  const [showDeleteWarning, setShowDeleteWarning] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMappingsOpen, setIsMappingsOpen] = useState(false);

  const COLUMNS: Column<SlackAppNotificationMapping>[] = [
    {
      Header: "Workspace",
      id: "team_name",
      accessor: "team_name",
      Cell: ({ row }) => {
        return <>{row.original.team_name}</>;
      },
    },
    {
      Header: "Channel Name",
      id: "channel_name",
      accessor: "channel_name",
      Cell: ({ row }) => {
        return <>{row.original.channel_name}</>;
      },
    },
    {
      Header: "Deployment",
      id: "deployment_slug",
      accessor: "deployment_slug",
      Cell: ({ row }) => {
        return <>{row.original.deployment_slug}</>;
      },
    },
    {
      Header: "Test",
      id: "test_button",
      accessor: "channel_id",
      Cell: ({ row }) => {
        return row.original.is_owner ? (
          <Button
            type="submit"
            intent="primary"
            onClick={() => testMapping(row.original.channel_id)}
          >
            Test
          </Button>
        ) : null;
      },
    },
    {
      Header: "Delete",
      id: "delete_button",
      accessor: "channel_id",
      Cell: ({ row }) => {
        return row.original.is_owner ? (
          <Button
            type="submit"
            intent="danger"
            onClick={() => removeMapping(row.original.channel_id)}
          >
            Delete
          </Button>
        ) : null;
      },
    },
  ];

  const updateIntegration = useMemo(
    () => (event: any) => {
      event.preventDefault();
      onUpdate();
    },
    [onUpdate]
  );

  const deleteIntegration = useMemo(
    () => (event: any) => {
      event.preventDefault();
      onDelete();
    },
    [onDelete]
  );

  const integrationConfig = CONFIG[integration.type];

  const card = (
    <Content>
      <Header>
        <HeaderLeft>
          <Label>
            {"icon" in integrationConfig ? (
              <>
                <FontAwesomeIcon icon={integrationConfig.icon} /> &nbsp;
              </>
            ) : null}
            {integrationConfig.name}
          </Label>
        </HeaderLeft>
        <Button
          intent="danger"
          minimal
          small
          onClick={() => setShowDeleteWarning(true)}
        >
          Remove integration
        </Button>
      </Header>
      <form onSubmit={updateIntegration}>
        <Header>
          <InputLabel>
            Client Id{" "}
            <Tooltip
              withArrow
              withinPortal
              position="right"
              label={
                <>
                  Every Slack app is issued a client id when the app is
                  registered by the distributor.
                  <br />
                  The value of client id cannot be changed.
                </>
              }
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Tooltip>
          </InputLabel>
          <WideInput
            readOnly
            placeholder="Client id for the Semgrep Slack app..."
            value={integration.client_id}
            className="fs-mask dd-privacy-mask"
          />
        </Header>
        <Header>
          <InputLabel>
            App Id{" "}
            <Tooltip
              withArrow
              withinPortal
              position="right"
              label={
                <>
                  Unique identifier for the Semgrep app on the Slack store.
                  <br />
                  The value of app id cannot be changed.
                </>
              }
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Tooltip>
          </InputLabel>
          <WideInput
            readOnly
            placeholder="App id for the Semgrep Slack app..."
            value={integration.app_id}
            className="fs-mask dd-privacy-mask"
          />
        </Header>
        {integration.enterprise_name && (
          <Header>
            <InputLabel>Enterprise Name</InputLabel>
            <WideInput
              readOnly
              placeholder="Enterprise name for the Semgrep Slack app installation..."
              value={integration.enterprise_name}
              className="fs-mask dd-privacy-mask"
            />
          </Header>
        )}
        {integration.enterprise_url && (
          <Header>
            <InputLabel>Enterprise URL</InputLabel>
            <WideInput
              readOnly
              placeholder="Enterprise URL for the Semgrep Slack app installation..."
              value={integration.enterprise_url}
              type={"url"}
              className="fs-mask dd-privacy-mask"
            />
          </Header>
        )}
        <Header>
          <Header>
            <InputLabel>Workspace</InputLabel>
            <WideInput
              readOnly
              placeholder={"Workspace name for the Slack app installation..."}
              value={integration.team_name}
              className="fs-mask dd-privacy-mask"
            />
          </Header>
          <Button
            type="submit"
            intent="primary"
            disabled={!integration.update_available}
          >
            Update
          </Button>
        </Header>
      </form>
      <Info>
        <Button
          icon={isOpen ? "caret-down" : "caret-right"}
          minimal
          small
          onClick={() => setIsOpen(!isOpen)}
        >
          Semgrep Slack app is now available in your workspace
        </Button>
        <Collapse isOpen={isOpen}>
          <>
            <h5>
              <b>Step 1:</b> Open your Slack workspace and navigate to the "Add
              apps" button.
            </h5>
            <h5>
              <b>Step 2:</b> Search for "Semgrep" and click to view the app
              homepage.
            </h5>
            <h5>
              <b>Step 3:</b> Start customizing the app for your use cases.
            </h5>
          </>
        </Collapse>
      </Info>
      <Info>
        <Button
          icon={isMappingsOpen ? "caret-down" : "caret-right"}
          minimal
          small
          onClick={() => setIsMappingsOpen(!isMappingsOpen)}
        >
          List of channels receiving Semgrep notifications.
        </Button>
        <Collapse isOpen={isMappingsOpen}>
          <>
            <DataTable columns={COLUMNS} data={mappings} />
            <br></br>
            <h5>To start receiving notifications on your Slack workspace,</h5>
            <h5>
              <b>Step 1:</b> Go to any of the public channels in your Slack
              workspace.
            </h5>
            <h5>
              <b>Step 2:</b> Run the{" "}
              <b>
                <code>/semgrep_subscribe</code>
              </b>{" "}
              command in the channel.
            </h5>
            <h5>
              <b>Step 3:</b> Select an organization in the modal and you're all
              set!
            </h5>
          </>
        </Collapse>
      </Info>
    </Content>
  );

  return (
    <>
      <Alert
        className="deleteIntegrationWarning"
        cancelButtonText="Cancel"
        confirmButtonText="Remove"
        icon="trash"
        style={{ width: "400px" }}
        intent="danger"
        isOpen={showDeleteWarning}
        onCancel={() => setShowDeleteWarning(false)}
        onConfirm={deleteIntegration}
      >
        <p>Are you sure you want to remove this integration?</p>
        <p>
          Choosing "Remove" will remove this integration from all policies that
          currently reference it.
        </p>
      </Alert>
      <IntegrationCard className="notification-option">{card}</IntegrationCard>
    </>
  );
};
